import type { Client } from "./client";

const waitForIce = (pc: RTCPeerConnection, timeoutMs = 2000): Promise<void> =>
  new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") return resolve();
    const done = () => {
      clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", onChange);
      resolve();
    };
    const onChange = () => pc.iceGatheringState === "complete" && done();
    const timer = setTimeout(done, timeoutMs);
    pc.addEventListener("icegatheringstatechange", onChange);
  });

export type Media = {
  pc: RTCPeerConnection;
  remote: MediaStream;
  close: () => void;
};

// negotiate sets up the RTCPeerConnection, exchanges SDP with the DigiCalls
// backend (POST /calls/{id}/webrtc), and auto-plays the remote audio.
export async function negotiate(
  client: Client,
  sessionId: string,
  callId: string,
  micStream: MediaStream | null,
  outputDeviceId: string | undefined,
  onClose: () => void,
): Promise<Media> {
  const pc = new RTCPeerConnection({ iceServers: await client.iceServers() });
  const sendTx = pc.addTransceiver("audio", { direction: "sendonly" });
  if (micStream) {
    const track = micStream.getAudioTracks()[0];
    if (track) await sendTx.sender.replaceTrack(track);
  }
  pc.addTransceiver("audio", { direction: "recvonly" });

  const remote = new MediaStream();
  const audioEl = typeof document !== "undefined" ? document.createElement("audio") : null;
  if (audioEl) {
    audioEl.autoplay = true;
    (audioEl as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
    audioEl.srcObject = remote;
  }
  pc.ontrack = (e) => {
    const stream = e.streams[0];
    if (stream) stream.getTracks().forEach((t) => remote.addTrack(t));
    else remote.addTrack(e.track);
    if (audioEl) {
      audioEl.srcObject = remote;
      const sink = audioEl as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      if (outputDeviceId && sink.setSinkId) sink.setSinkId(outputDeviceId).catch(() => {});
      audioEl.play().catch(() => {});
    }
  };
  pc.onconnectionstatechange = () => {
    if (["failed", "closed", "disconnected"].includes(pc.connectionState)) onClose();
  };

  await pc.setLocalDescription(await pc.createOffer());
  await waitForIce(pc);
  const { sdp_answer } = await client.post<{ sdp_answer: string }>(
    `/api/sessions/${sessionId}/calls/${callId}/webrtc`,
    { sdp_offer: pc.localDescription!.sdp },
  );
  await pc.setRemoteDescription({ type: "answer", sdp: sdp_answer });

  const close = () => {
    try {
      pc.getSenders().forEach((s) => s.track?.stop());
      pc.close();
    } catch {
      /* noop */
    }
    if (audioEl) {
      audioEl.srcObject = null;
      audioEl.remove();
    }
  };
  return { pc, remote, close };
}
